'use client';

import { useCallback, useState, useEffect, useRef } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Image from 'next/image';
import Modal from 'react-modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faXmark } from '@awesome.me/kit-94e593bf23/icons/sharp/light';
import { faChevronLeft as faChevronLeftLight, faChevronRight as faChevronRightLight } from '@awesome.me/kit-94e593bf23/icons/sharp/light';
import { faExpand } from '@awesome.me/kit-94e593bf23/icons/classic/light';
import { faHiveQuickShip } from '@awesome.me/kit-94e593bf23/icons/kit/custom';
import Tooltip from '@/app/_components/Tooltip';
import { useAppContext } from '@/app/_lib/_context';
import { decodeEntities } from '@/app/_lib/services/ClientUtil';
import Price from '@/app/_components/Price';
import addToCartAction from '@/app/_lib/actions/AddToCartAction';
import { useCart } from '@/app/_lib/hooks';

// The full variant list ships to the page either way (the toolbar needs it),
// but rendering every card put 400+ variants of markup in the document on the
// biggest products. Render a window instead: the first RENDER_INITIAL cards,
// growing by RENDER_BATCH as the next arrow reaches the rendered edge. The
// modal viewer still navigates the complete list - it renders one image at a
// time, so it costs nothing.
const RENDER_INITIAL = 15;
const RENDER_BATCH = 15;
const VariantsCarousel = ({
  sub_product,
  initialVariants = []
}) => {
  const {
    openCartModal
  } = useAppContext();
  const {
    refresh
  } = useCart();
  useEffect(() => {
    Modal.setAppElement('#app');
  }, []);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: 'start',
    slidesToScroll: 1,
    containScroll: 'trimSnaps'
  });

  // Use server-rendered variants
  const variants = initialVariants;
  const [addingToCart, setAddingToCart] = useState({});
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Windowed rendering: only renderCount cards exist in the DOM.
  // renderedVariants is a prefix of variants, so strip indexes match the
  // full-array indexes the modal uses.
  const [renderCount, setRenderCount] = useState(RENDER_INITIAL);
  const renderedVariants = variants.slice(0, renderCount);
  const hasMoreToRender = renderCount < variants.length;
  const pendingScrollRef = useRef(false);

  // Carousel scroll state
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  // Floating cursor state for carousel expand icon
  const [cursorPos, setCursorPos] = useState({
    x: 0,
    y: 0
  });
  const [cursorVisible, setCursorVisible] = useState(false);

  // Floating cursor state for modal navigation
  const [modalCursorPos, setModalCursorPos] = useState({
    x: 0,
    y: 0
  });
  const [modalCursorVisible, setModalCursorVisible] = useState(false);
  const [modalCursorSide, setModalCursorSide] = useState('right');
  const modalImageContainerRef = useRef(null);

  // Get current selected item from index
  const selectedItem = variants[selectedIndex] || null;
  const handleImageMouseMove = useCallback(e => {
    setCursorPos({
      x: e.clientX,
      y: e.clientY
    });
  }, []);
  const handleImageMouseEnter = useCallback(() => {
    setCursorVisible(true);
  }, []);
  const handleImageMouseLeave = useCallback(() => {
    setCursorVisible(false);
  }, []);

  // Modal image navigation handlers
  const handleModalImageMouseMove = useCallback(e_0 => {
    if (!modalImageContainerRef.current) return;
    const rect = modalImageContainerRef.current.getBoundingClientRect();
    const x = e_0.clientX - rect.left;
    setModalCursorPos({
      x: e_0.clientX,
      y: e_0.clientY
    });
    setModalCursorSide(x < rect.width / 2 ? 'left' : 'right');
  }, []);
  const handleModalImageMouseEnter = useCallback(() => {
    setModalCursorVisible(true);
  }, []);
  const handleModalImageMouseLeave = useCallback(() => {
    setModalCursorVisible(false);
  }, []);
  const handleModalImageClick = useCallback(() => {
    if (variants.length <= 1) return;
    if (modalCursorSide === 'left') {
      setSelectedIndex(prev => prev === 0 ? variants.length - 1 : prev - 1);
    } else {
      setSelectedIndex(prev_0 => prev_0 === variants.length - 1 ? 0 : prev_0 + 1);
    }
  }, [modalCursorSide, variants.length]);
  const openItemModal = item => {
    setCursorVisible(false);
    const index = variants.findIndex(v => v.id === item.id);
    setSelectedIndex(index >= 0 ? index : 0);
    setIsModalOpen(true);
  };
  const closeModal = () => {
    setIsModalOpen(false);
    setModalCursorVisible(false);
  };

  // Keyboard navigation for modal
  useEffect(() => {
    if (!isModalOpen || variants.length <= 1) return;
    const handleKeyDown = e_1 => {
      if (e_1.key === 'ArrowLeft') {
        setSelectedIndex(prev_1 => prev_1 === 0 ? variants.length - 1 : prev_1 - 1);
      } else if (e_1.key === 'ArrowRight') {
        setSelectedIndex(prev_2 => prev_2 === variants.length - 1 ? 0 : prev_2 + 1);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isModalOpen, variants.length]);
  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);
  const scrollNext = useCallback(() => {
    if (!emblaApi) return;

    // Nearing the rendered edge with more variants unrendered: grow the
    // window, then scroll once the new slides are in (reInit effect below)
    const currentIndex = emblaApi.selectedScrollSnap();
    const totalSnaps = emblaApi.scrollSnapList().length;
    if (hasMoreToRender && currentIndex >= totalSnaps - 3) {
      pendingScrollRef.current = true;
      setRenderCount(count => Math.min(count + RENDER_BATCH, variants.length));
      return;
    }
    emblaApi.scrollNext();
  }, [emblaApi, hasMoreToRender, variants.length]);

  // Pick up newly rendered slides, and finish the click that grew the window
  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.reInit();
    if (pendingScrollRef.current) {
      pendingScrollRef.current = false;
      emblaApi.scrollNext();
    }
  }, [emblaApi, renderedVariants.length]);

  // Update scroll button states as user scrolls
  const updateScrollButtons = useCallback(() => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    // The next arrow stays live while unrendered variants remain
    setCanScrollNext(emblaApi.canScrollNext() || hasMoreToRender);
  }, [emblaApi, hasMoreToRender]);
  useEffect(() => {
    if (!emblaApi) return;
    updateScrollButtons();
    emblaApi.on('select', updateScrollButtons);
    emblaApi.on('reInit', updateScrollButtons);
    return () => {
      emblaApi.off('select', updateScrollButtons);
      emblaApi.off('reInit', updateScrollButtons);
    };
  }, [emblaApi, updateScrollButtons]);
  const getImageUrl = item_0 => {
    if (item_0.image_url) {
      return item_0.image_url;
    }
    if (item_0.swatches && item_0.swatches.length > 0) {
      const firstSwatch = item_0.swatches[0];
      if (firstSwatch.swatch_thumb) {
        return `swatch_photos/${firstSwatch.swatch_thumb}`;
      }
      if (firstSwatch.swatch_photo) {
        return `swatch_photos/${firstSwatch.swatch_photo}`;
      }
    }
    if (sub_product.thumb_1) {
      return `thumbs/${sub_product.thumb_1}`;
    }
    if (sub_product.product_photo) {
      return sub_product.product_photo;
    }
    return null;
  };
  const handleAddToCart = async item_1 => {
    setAddingToCart(prev_3 => ({
      ...prev_3,
      [item_1.id]: true
    }));

    // Format options from array to object with selection_id as keys
    const optionsObject = {};
    if (item_1.options && Array.isArray(item_1.options)) {
      item_1.options.forEach(option => {
        optionsObject[option.selection_id] = {
          selection_id: option.selection_id,
          option_id: option.option_id,
          option_name: option.option_name,
          selection_label: option.selection_label,
          option_price: option.option_price,
          option_photo: option.option_photo
        };
      });
    }

    // Format swatches from array to object with swatch_set_id as keys
    const swatchesObject = {};
    if (item_1.swatches && Array.isArray(item_1.swatches)) {
      item_1.swatches.forEach(swatch => {
        swatchesObject[swatch.swatch_set_id] = {
          swatch_index: swatch.swatch_index,
          swatch_id: swatch.swatch_id,
          swatch_set_id: swatch.swatch_set_id,
          swatch_line_id: swatch.swatch_line_id,
          swatch_name: swatch.swatch_name,
          swatch_price: swatch.swatch_price,
          swatch_photo: swatch.swatch_photo,
          swatch_set_label: swatch.swatch_set_label,
          xref_id: swatch.xref_id
        };
      });
    }

    // Build variant object if image is available
    const variant = item_1.image_url ? {
      file_name: item_1.image_url,
      description: item_1.variant_description,
      sku: item_1.sku
    } : null;

    // Build cart object
    const cartObject = {
      quantity: 1,
      product: {
        sub_product_id: item_1.sub_product_id,
        options: optionsObject,
        swatches: swatchesObject,
        ...(variant && {
          variant: variant
        })
      }
    };
    const response = await addToCartAction(cartObject);
    if (response.success) {
      refresh(response.cart);
      openCartModal({
        message: 'The item was added to your cart.',
        sub_product: sub_product
      });
    } else {
      console.error('Failed to add item to cart');
    }
    setAddingToCart(prev_4 => ({
      ...prev_4,
      [item_1.id]: false
    }));
  };
  if (variants.length === 0) {
    return null;
  }
  return <div className="site-padding mb-10 lg:mb-8 w-full min-[1810px]:px-0 relative">
            <div className="mb-3">
                <h2 className="leading-[1] text-2xl font-extralight capitalize">{decodeEntities(sub_product.sub_product_name)} Options ({variants.length})</h2>
                <p className="text-xs text-ink-light m-0">Pre-configured variants of this product</p>
            </div>

            <div className="flex items-center -mr-3">
                {/* Left Arrow - show both arrows when either direction can scroll */}
                {(canScrollPrev || canScrollNext) && <button onClick={scrollPrev} disabled={!canScrollPrev} aria-label="Previous items" className={`absolute left-[calc(var(--site-padding)+0.75rem)] top-1/3 z-100 shrink-0 mr-2 ${canScrollPrev ? 'cursor-pointer' : 'cursor-default opacity-30'}`}>
                        <div className="w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center hover:shadow-lg transition-shadow">
                            <FontAwesomeIcon icon={faChevronLeft} className="text-dark text-sm" />
                        </div>
                    </button>}

                {/* Carousel */}
                <div className="flex-1 min-w-0">
                    <div ref={emblaRef} className="overflow-hidden">
                        <div className="flex">
                            {renderedVariants.map((item_2, index_0) => <div key={item_2.id || index_0} className={`flex-none pr-3 ${variants.length <= 5 ? 'w-1/2 sm:w-1/3 md:w-1/4 xl:w-1/5 2xl:w-[16.666%]' : 'w-1/2 sm:w-1/3 md:w-1/4 lg:w-1/5 xl:w-1/6 2xl:w-[14.285%]'}`}>
                                    <div className="group h-full flex flex-col">
                                        {/* Image */}
                                        <div className="relative w-full aspect-square mb-3 flex items-center justify-center cursor-none bg-gray-3 overflow-hidden" onClick={() => openItemModal(item_2)} onMouseMove={handleImageMouseMove} onMouseEnter={handleImageMouseEnter} onMouseLeave={handleImageMouseLeave}>
                                            {getImageUrl(item_2) && <div className="overlay-block lg:transition-all lg:duration-400 lg:group-hover:scale-105">
                                                    <Image src={`${process.env.NEXT_PUBLIC_RESOURCE_URL}/${getImageUrl(item_2)}`} alt={item_2.variant_description || decodeEntities(item_2.sub_product_name)} width={120} height={120} sizes="120px" className="max-w-[120px] max-h-[120px] object-contain pointer-events-none" />
                                                </div>}
                                        </div>

                                        {/* Variant Info */}
                                        <div className="text-left flex-1 flex flex-col">
                                            {item_2.on_sale == 1 && item_2.discount_percentage > 0 && <div className="text-2xs mb-3 text-red">
                                                    <span className="font-semibold">Sale</span> {item_2.discount_percentage}% off
                                                </div>}
                                            <div className="font-medium text-sm capitalize mb-2">
                                                {decodeEntities(sub_product.sub_product_name)}
                                            </div>

                                            {/* Options */}
                                            {item_2.options?.length > 0 && <div className="mb-1 text-2xs">
                                                    {item_2.options.map((option_0, idx) => <div key={idx}>
                                                            <span className="text-light capitalize">{option_0.selection_label}:</span> <span className="text-dark">{option_0.option_name}</span>
                                                        </div>)}
                                                </div>}

                                            {/* Swatches */}
                                            {item_2.swatches?.length > 0 && <div className="mb-1 text-2xs">
                                                    {item_2.swatches.map((swatch_0, idx_0) => <div key={idx_0}>
                                                            <span className="text-light capitalize">{swatch_0.swatch_set_label}:</span> <span className="text-dark">{swatch_0.swatch_name}</span>
                                                        </div>)}
                                                </div>}

                                            <div className="text-xs mt-1 mb-4">
                                                <Price amount={item_2.retail_price || sub_product.retail_price} />
                                                {item_2.on_sale == 1 && parseFloat(item_2.original_price) > 0 && <span className="line-through text-light ml-1">
                                                        <Price amount={item_2.original_price} />
                                                    </span>}
                                                {(item_2.quick_ship_quantity > 0 || item_2.inventory_quantity > 0) && <Tooltip content="This variant is in stock and ready to ship!" side="top">
                                                        <span>
                                                            <FontAwesomeIcon icon={faHiveQuickShip} className="ml-1 text-base text-primary cursor-pointer" />
                                                        </span>
                                                    </Tooltip>}
                                            </div>

                                            {/* Spacer to push button to bottom */}
                                            <div className="flex-1"></div>

                                            {/* Add to Cart Button */}
                                            <button onClick={() => handleAddToCart(item_2)} disabled={addingToCart[item_2.id]} className="btn btn-xs btn-extra-light w-full">
                                                {addingToCart[item_2.id] ? 'Adding...' : 'Add to Cart'}
                                            </button>
                                        </div>
                                    </div>
                                </div>)}
                        </div>
                    </div>
                </div>

                {/* Right Arrow - show both arrows when either direction can scroll */}
                {(canScrollPrev || canScrollNext) && <button onClick={scrollNext} disabled={!canScrollNext} aria-label="Next items" className={`absolute right-[calc(var(--site-padding)+0.75rem)] top-1/3 shrink-0 ml-2 ${canScrollNext ? 'cursor-pointer' : 'cursor-default opacity-30'}`}>
                        <div className="w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center hover:shadow-lg transition-shadow">
                            <FontAwesomeIcon icon={faChevronRight} className="text-dark text-sm" />
                        </div>
                    </button>}
            </div>

            {/* Floating cursor expand icon */}
            {cursorVisible && <div className="pointer-events-none fixed z-[100] flex items-center justify-center w-10 h-10 rounded-full bg-white shadow-md" style={{
      left: cursorPos.x,
      top: cursorPos.y,
      transform: 'translate(-50%, -50%)'
    }}>
                    <FontAwesomeIcon icon={faExpand} className="text-sm text-dark" />
                </div>}

            {/* Item Detail Modal */}
            <Modal isOpen={isModalOpen} onRequestClose={closeModal} contentLabel="Product Variant" overlayClassName="fixed inset-0 z-[1000] flex items-center justify-center bg-modal-overlay p-6" className="relative bg-white rounded-lg w-full max-w-[500px] outline-none" bodyOpenClassName="ReactModal__Body--open" htmlOpenClassName="ReactModal__Html--open">
                {selectedItem && <div className="p-5">
                        {/* Close button */}
                        <button onClick={closeModal} className="absolute top-3 right-3 z-10 w-8 h-8 flex items-center justify-center bg-white/80 rounded-full hover:bg-white transition-colors" aria-label="Close">
                            <FontAwesomeIcon icon={faXmark} className="text-lg" />
                        </button>

                        {/* Image - constrained height */}
                        <div ref={modalImageContainerRef} className={`p-3 relative select-none flex items-center justify-center ${variants.length > 1 ? 'lg:cursor-none' : ''}`} style={{
          height: 'min(50vh, 400px)'
        }} onMouseMove={handleModalImageMouseMove} onMouseEnter={handleModalImageMouseEnter} onMouseLeave={handleModalImageMouseLeave} onClick={handleModalImageClick}>
                            <div className="overlay-block h-full w-full flex items-center justify-center relative p-4">
                                {getImageUrl(selectedItem) && <Image src={`${process.env.NEXT_PUBLIC_RESOURCE_URL}/${getImageUrl(selectedItem)}`} alt={selectedItem.variant_description || decodeEntities(selectedItem.sub_product_name)} quality={90} width={400} height={400} sizes="400px" className="max-w-full max-h-full object-contain pointer-events-none" />}

                                {/* Nav arrows */}
                                {variants.length > 1 && <>
                                        <button onClick={e_2 => {
                e_2.stopPropagation();
                setSelectedIndex(prev_5 => prev_5 === 0 ? variants.length - 1 : prev_5 - 1);
              }} className="lg:hidden absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 flex items-center justify-center z-20" aria-label="Previous variant">
                                            <FontAwesomeIcon icon={faChevronLeft} className="text-dark text-sm" />
                                        </button>
                                        <button onClick={e_3 => {
                e_3.stopPropagation();
                setSelectedIndex(prev_6 => prev_6 === variants.length - 1 ? 0 : prev_6 + 1);
              }} className="lg:hidden absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 flex items-center justify-center z-20" aria-label="Next variant">
                                            <FontAwesomeIcon icon={faChevronRight} className="text-dark text-sm" />
                                        </button>
                                    </>}

                            </div>
                        </div>

                        {/* Item counter - outside image container */}
                        {variants.length > 1 && <div className="text-right text-xs text-light mt-1 pr-3">
                                {selectedIndex + 1} / {variants.length}
                            </div>}

                        {/* Product info */}
                        <div className="mt-4">
                            {selectedItem.on_sale == 1 && selectedItem.discount_percentage > 0 && <div className="text-2xs mb-3 text-red">
                                    <span className="font-semibold">Sale</span> {selectedItem.discount_percentage}% off
                                </div>}
                            <div className="font-medium text-base capitalize">
                                {decodeEntities(sub_product.sub_product_name)}
                            </div>

                            {selectedItem.options?.length > 0 && <div className="mt-2 text-sm">
                                    {selectedItem.options.map((option_1, idx_1) => <div key={idx_1}>
                                            <span className="text-light capitalize">{option_1.selection_label}:</span> <span className="text-dark">{option_1.option_name}</span>
                                        </div>)}
                                </div>}

                            {selectedItem.swatches?.length > 0 && <div className="mt-2 text-sm">
                                    {selectedItem.swatches.map((swatch_1, idx_2) => <div key={idx_2}>
                                            <span className="text-light capitalize">{swatch_1.swatch_set_label}:</span> <span className="text-dark">{swatch_1.swatch_name}</span>
                                        </div>)}
                                </div>}

                            <div className="text-sm mt-2 mb-4">
                                <Price amount={selectedItem.retail_price || sub_product.retail_price} />
                                {selectedItem.on_sale == 1 && parseFloat(selectedItem.original_price) > 0 && <span className="line-through text-light ml-2">
                                        <Price amount={selectedItem.original_price} />
                                    </span>}
                                {(selectedItem.quick_ship_quantity > 0 || selectedItem.inventory_quantity > 0) && <Tooltip content="This variant is in stock and ready to ship!" side="top">
                                        <span>
                                            <FontAwesomeIcon icon={faHiveQuickShip} className="ml-1 text-base text-primary" />
                                        </span>
                                    </Tooltip>}
                            </div>

                            <button onClick={() => {
            handleAddToCart(selectedItem);
            closeModal();
          }} disabled={addingToCart[selectedItem.id]} className="btn btn-sm btn-extra-light w-full">
                                {addingToCart[selectedItem.id] ? 'Adding...' : 'Add to Cart'}
                            </button>
                        </div>
                    </div>}
            </Modal>

            {/* Floating cursor for modal navigation - rendered outside modal to avoid stacking context issues */}
            {modalCursorVisible && variants.length > 1 && <div className="pointer-events-none fixed z-[1100] flex items-center justify-center w-12 h-12 rounded-full bg-white shadow-md" style={{
      left: modalCursorPos.x,
      top: modalCursorPos.y,
      transform: 'translate(-50%, -50%)'
    }}>
                    <FontAwesomeIcon icon={modalCursorSide === 'left' ? faChevronLeftLight : faChevronRightLight} className="text-xl text-dark" />
                </div>}
        </div>;
};
export default VariantsCarousel;